import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ItemDetailSize,
  ReturnItemParams,
} from "@redotech/redo-customer-portal-shared/util";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import classNames from "classnames";
import { MaybeDisplayHtml } from "../returns/components/maybe-display-html";
import { MerchantOverrideItemAdjustments } from "../submission/screens/review/merchant-overrides/merchant-override-item-adjustments";
import { MerchantOverrideItemRejectMessage } from "../submission/screens/review/merchant-overrides/merchant-override-item-reject-message";
import { getItemDetail } from "./get-item-detail";
import * as itemDetailCss from "./item-detail.module.css";
import { PortalMetafieldBadges } from "./portal-metafield-badges";

export function ItemDetail({
  item,
  isVertical,
  size = ItemDetailSize.MEDIUM,
  hideTextStrings = false,
  hideQuantity = false,
  hidePrice = false,
  hideDiscount = false,
  overrides,
  isMerchant = false,
  showQuantityOnImage = false,
  showSquareImage = false,
  showQuantityText = false,
  settings,
}: {
  item?: ReturnItemParams;
  isVertical?: boolean;
  size?: ItemDetailSize;
  hideTextStrings?: boolean;
  hideQuantity?: boolean;
  hidePrice?: boolean;
  hideDiscount?: boolean;
  overrides?: MerchantOverrides;
  isMerchant?: boolean;
  showQuantityOnImage?: boolean;
  showSquareImage?: boolean;
  showQuantityText?: boolean;
  settings: CustomerPortalAppSettings | undefined;
}) {
  const { t, i18n } = useTranslation();
  const screen = useScreenType(762);
  const shouldBeVertical = isVertical ?? screen === ScreenType.DESKTOP;
  const { imageUri, texts, loading, formattedItemValue, formattedPrice } =
    getItemDetail(
      { item, isVertical: shouldBeVertical, hideQuantity, hidePrice, settings },
      t,
      i18n.language,
    );

  if (loading) {
    return null;
  }
  const { title, subtitle, textStrings, badgeText } = texts;

  return (
    <Flex
      bgColor={item?.isPreRejected && !isMerchant ? "disabled" : "none"}
      flexDirection="column"
      gap="lg"
      w="full"
    >
      <Flex flex={1} justify="space-between">
        <Flex gap={shouldBeVertical ? "lg" : "xl"} w="full">
          {!!imageUri && (
            <div className={itemDetailCss.imageContainer}>
              <img
                alt={title}
                className={classNames(itemDetailCss.image, {
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [itemDetailCss.small]: size === ItemDetailSize.SMALL,
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  [itemDetailCss.squareImage]: showSquareImage,
                })}
                src={imageUri}
                style={{ opacity: item?.isPreRejected ? 0.6 : 1 }}
              />
              {showQuantityOnImage && item?.quantity && (
                <div className={itemDetailCss.quantityBadge}>
                  {item.quantity}
                </div>
              )}
            </div>
          )}
          <Flex w="full">
            <Flex
              dir="column"
              gap={size === ItemDetailSize.SMALL ? "xs" : "none"}
              justify={shouldBeVertical ? "flex-start" : "center"}
              w="full"
            >
              <Flex justify="space-between" w="full">
                <Text
                  className={itemDetailCss.itemTitle}
                  fontSize="md"
                  fontWeight="medium"
                  textColor={item?.isPreRejected ? "disabled" : "primary"}
                >
                  {title}
                </Text>
                {showQuantityText && item?.quantity && (
                  <Text
                    fontSize="md"
                    fontWeight="medium"
                    textColor={item?.isPreRejected ? "disabled" : "primary"}
                  >
                    x {item.quantity}
                  </Text>
                )}
              </Flex>

              <Flex dir="column" gap="none">
                {subtitle && (
                  <Text
                    fontSize="xs"
                    fontWeight="regular"
                    key="subtitle"
                    textColor={item?.isPreRejected ? "disabled" : "tertiary"}
                  >
                    {subtitle}
                  </Text>
                )}
                {!hideTextStrings &&
                  textStrings.map((text, index) => (
                    <Text
                      fontSize="xs"
                      fontWeight="regular"
                      key={index}
                      textColor={item?.isPreRejected ? "disabled" : "tertiary"}
                    >
                      {text}
                    </Text>
                  ))}
                <PortalMetafieldBadges
                  values={item?.portalDisplayMetafieldValues}
                />
                {item?.isPreRejected && isMerchant && (
                  <MerchantOverrideItemRejectMessage
                    merchantCreatedPreRejectMessage={
                      texts.merchantCreatedPreRejectMessage || ""
                    }
                  />
                )}
                {item?.isPreRejected && item.rejectMessage !== undefined ? (
                  <MaybeDisplayHtml
                    html={item.rejectMessage}
                    textProps={{
                      fontSize: "xs",
                      fontWeight: "regular",
                      textColor: "disabled",
                    }}
                  />
                ) : null}

                <Flex
                  dir="row"
                  gap="sm"
                  py={shouldBeVertical ? "md" : "xs"}
                  wrap="wrap"
                >
                  {badgeText ? (
                    <RedoBadge color="success" text={badgeText} />
                  ) : null}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {shouldBeVertical && !hidePrice ? (
          <Flex dir="row" gap="sm">
            {formattedPrice && !hideDiscount ? (
              <Tooltip placement="top" title={texts.discountExplanation}>
                <Flex dir="row" gap="xs">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    style={{ textDecoration: "line-through" }}
                    textColor={item?.isPreRejected ? "disabled" : "secondary"}
                  >
                    {formattedPrice}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    textColor={item?.isPreRejected ? "disabled" : "primary"}
                  >
                    {formattedItemValue}
                  </Text>
                </Flex>
              </Tooltip>
            ) : (
              <Text
                fontSize="sm"
                fontWeight="medium"
                textColor={item?.isPreRejected ? "disabled" : "primary"}
              >
                {hideDiscount
                  ? formattedPrice || formattedItemValue
                  : formattedItemValue}
              </Text>
            )}
          </Flex>
        ) : null}
      </Flex>
      {overrides && (
        <MerchantOverrideItemAdjustments
          itemReturnValue={
            item?.itemValue ? parseFloat(item.itemValue) : undefined
          }
          overrides={overrides}
          pendingReturnItemId={item?.pendingReturnItemId}
        />
      )}
    </Flex>
  );
}
